import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState<{
    text: string;
    type: 'error' | 'success';
    visible: boolean;
  }>({ text: '', type: 'error', visible: false });
  const navigate = useNavigate();

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    const userRole = localStorage.getItem('userRole');
    if(userId && userRole) {
      navigate('/')
    }
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        document.getElementById('login-button')?.click();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  const showMessage = (text: string, type: 'error' | 'success') => {
    setMessage({ text, type, visible: true });
    setTimeout(() => {
      setMessage(prev => ({ ...prev, visible: false }));
    }, 2000);
  };

  const handleLogin = async () => {
    if (!email || !password) {
      showMessage('Per favore, compila tutti i campi.', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/login`, {
        email,
        password,
      });

      if (response.data.user) {
        localStorage.setItem('userId', response.data.user.id);
        localStorage.setItem('userRole', response.data.role);
        showMessage('Connessione effettuata con successo!', 'success');
        setTimeout(() => navigate('/'), 200);
      } else {
        showMessage('Credenziali non valide', 'error');
      }
    } catch (error) {
      console.error('Errore durante la connessione:', error);
      showMessage('Si è verificato un errore durante la connessione.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-indigo-500 to-purple-500">
      <div className="w-full max-w-md">
        <div className="bg-white shadow-xl rounded-2xl p-8 transform hover:-translate-y-1 transition-transform duration-300">
          <div className="flex flex-col items-center gap-4 mb-8">
            <h1 className="text-3xl font-bold text-gray-800">Accedi</h1>
            <p className="text-sm text-gray-400">Benvenuto! Accedi per continuare</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-4 flex items-center justify-between animate-in fade-in slide-in-from-top-2">
              <span>{error}</span>
              <button
                onClick={() => setError(null)}
                className="text-red-600 hover:text-red-700 transition-colors"
              >
                ×
              </button>
            </div>
          )}

          {message.visible && (
            <div 
              className={`mb-2 rounded-md px-3 py-2 animate-in fade-in slide-in-from-top-2 ${
                message.type === 'error' 
                  ? 'bg-red-50 border border-red-200 text-red-600'
                  : 'bg-green-50 border border-green-200 text-green-600'
              }`}
            >
              {message.text}
            </div>
          )}

          <div className="space-y-6">
            <div className="space-y-2">
              <input
                type="email"
                placeholder="Indirizzo email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-3 text-sm bg-white border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:border-transparent outline-none transition-colors duration-200 ease-in-out placeholder:text-gray-400"
              />
            </div>

            <div className="space-y-2 relative">
              <div className="flex items-center">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full p-3 text-sm bg-white border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:border-transparent outline-none transition-colors duration-200 ease-in-out placeholder:text-gray-400"
                />
                <button
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  title={showPassword ? 'Nascondi password' : 'Mostra password'}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <button
              id="login-button"
              onClick={handleLogin}
              disabled={isLoading}
              className={`w-full py-3 px-4 rounded-lg text-white font-medium transition-colors duration-200 disabled:opacity-50 hover:bg-indigo-600 active:bg-indigo-700`}
              style={{
                backgroundColor: isLoading ? '#94A3B8' : '#3B82F6',
                cursor: isLoading ? 'not-allowed' : 'pointer'
              }}
            >
              {isLoading ? 'Accesso in corso...' : 'Accedi'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;